import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Permission } from '@core/interfaces/i_permission';
import { Profile } from '@core/interfaces/i_profile';
import { MessageService } from 'primeng/api';
import { Permissions } from './s_permissions';
import { Supabase } from './supabase';

@Injectable({
  providedIn: 'root',
})
export class Auth {
  private supabase = inject(Supabase);
  private permissions = inject(Permissions);
  private router = inject(Router);
  private messageService = inject(MessageService);

  isAuth = signal(false);
  profile = signal<Profile | null>(null);
  private _cachedProfile: Profile | null = null;
  private _isIntentionalSignOut = false;

  // Reactive Resource for Permissions
  // Owned by Auth because it depends on the user profile signal
  permissionsResource = resource<Permission[], { role: string | undefined }>({
    params: () => ({ role: this.profile()?.role ?? undefined }),
    loader: async ({ params }) => {
      if (!params.role) return [];
      return await this.permissions.fetchByRole(params.role);
    },
  });

  // Computed signals for direct access
  allPermissions = computed(() => this.permissionsResource.value() ?? []);

  constructor() {
    this.supabase.client.auth.onAuthStateChange((event, session) => {
      console.log(`[Auth Event] ${event}`, {
        hasSession: !!session,
        expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : 'N/A',
        expiresIn: session?.expires_in,
        currentTime: new Date().toLocaleString(),
        tokenPreview: session?.access_token ? `${session.access_token.substring(0, 15)}...` : 'No token'
      });
      
      this.isAuth.set(!!session);
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        this.clearSecurityState();
        
        if (!this._isIntentionalSignOut) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Sesión expirada',
            detail: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
            life: 6000,
          });
        }
        
        // Reseteamos la bandera
        this._isIntentionalSignOut = false;
        
        this.router.navigate(['/welcome']);
      }
    });

    // Restore profile from localStorage on start
    const stored = this.getStoredProfile();
    if (stored) {
      this._cachedProfile = stored;
      this.profile.set(stored);
    }
  }

  // Helper methods delegating to Permissions service logic
  hasPermission(resource: string, action: string): boolean {
    return this.permissions.hasPermission(this.allPermissions(), resource, action);
  }

  hasPermissionForRoute(route: string): boolean {
    return this.permissions.hasPermissionForRoute(this.allPermissions(), route);
  }

  getVisibleRoutes(): Permission[] {
    return this.permissions.getVisibleRoutes(this.allPermissions());
  }

  private clearSecurityState() {
    this._cachedProfile = null;
    this.profile.set(null);
    localStorage.removeItem('profile');
    this.permissionsResource.reload();
  }

  async signIn(email: string, password: string) {
    const res = await this.supabase.client.auth.signInWithPassword({
      email,
      password,
    });

    if (res.error) {
      console.error('[Auth signIn] Error:', res.error);
      throw new Error(res.error.message);
    }

    console.log('[Auth signIn] Success', {
      user: res.data.user?.id,
      session: res.data.session ? {
        expiresAt: new Date((res.data.session.expires_at || 0) * 1000).toLocaleString(),
        expiresIn: res.data.session.expires_in
      } : 'No session returned in signIn'
    });

    this.isAuth.set(true);
    return res.data.user;
  }

  async signOut() {
    this._isIntentionalSignOut = true;
    this.clearSecurityState();
    this.isAuth.set(false);
    return await this.supabase.client.auth.signOut();
  }

  /**
   * Ensures the current session has a valid, non-expired token.
   * Call this BEFORE any critical Supabase operation (inserts, updates, etc.)
   * to prevent silent session termination.
   */
  async ensureValidSession(): Promise<void> {
    const { data, error } = await this.supabase.client.auth.refreshSession();

    if (error || !data.session) {
      throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }
  }

  async getSession() {
    const sesion = await this.supabase.client.auth.getSession();
    const hasSession = sesion.data.session != null;

    this.isAuth.set(hasSession);

    if (!hasSession) {
      this.clearSecurityState();
    }

    return sesion;
  }

  async getUserProfile(userId: string): Promise<Profile> {
    const { data, error } = await this.supabase.client
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      throw new Error(error.message);
    }

    const userData = data as Profile;

    if (!userData.full_name || !userData.role) {
      console.warn('Perfil incompleto:', userData);
    }

    return userData;
  }

  async getCurrentUserWithProfile() {
    const { data } = await this.getSession();

    if (!data.session) {
      this._cachedProfile = null;
      return null;
    }

    // 1. Check in-memory cache
    if (this._cachedProfile && this._cachedProfile.user_id === data.session.user.id) {
      return {
        user: data.session.user,
        profile: this._cachedProfile,
      };
    }

    // 2. Check localStorage cache
    const storedProfile = this.getStoredProfile();
    if (storedProfile && storedProfile.user_id === data.session.user.id) {
      this._cachedProfile = storedProfile;
      this.profile.set(storedProfile);
      return {
        user: data.session.user,
        profile: storedProfile,
      };
    }

    // 3. Fetch from DB
    try {
      const profile = await this.getUserProfile(data.session.user.id);
      this._cachedProfile = profile;
      this.storageProfile(profile);

      return {
        user: data.session.user,
        profile: profile,
      };
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async onAuthStateChange(callback: any) {
    return await this.supabase.client.auth.onAuthStateChange(callback);
  }

  storageProfile(profileData: Profile) {
    this.profile.set(profileData);
    return localStorage.setItem('profile', JSON.stringify(profileData));
  }

  getStoredProfile(): Profile | null {
    const profile = localStorage.getItem('profile');
    return profile ? (JSON.parse(profile) as Profile) : null;
  }

  async sendPasswordResetEmail(email: string) {
    const { error } = await this.supabase.client.functions.invoke('account-reset-password', {
      body: { email },
    });

    if (error) {
      throw new Error(error.message || 'Error al enviar el correo de recuperación');
    }
  }

  async confirmPasswordReset(email: string, code: string, newPassword: string) {
    const { error } = await this.supabase.client.functions.invoke(
      'account-reset-password-confirm',
      {
        body: {
          email,
          code,
          newPassword,
        },
      }
    );

    if (error) {
      throw new Error(error.message || 'Error al reestablecer la contraseña');
    }
  }
}
